import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, h2, h4, paragraphSmall } from '@helpers/typography';
import { CommonButtonTextStyles, DefaultButtonStyles, } from '@stories/Components/Buttons/Button/Button.styles';
import { CommonDateWrapperStyles } from '@stories/Components/Misc/Forms/Inputs//Date/Date.styles';
import { CommonCheckboxContainerStyles, CommonCheckboxLabelStyles, } from '@stories/Components/Misc/Forms/Inputs/CheckboxList/CheckboxList.styles';
import { CommonRadioListContainerStyles, CommonRadioListLabelStyles, } from '@stories/Components/Misc/Forms/Inputs/RadioList/RadioList.styles';
import { CommonDropdownWrapperStyles, CommonSelectStyles, } from '@stories/Components/Misc/Forms/Inputs/Select/Select.styles';
import { CommonTextareaStyles } from '@stories/Components/Misc/Forms/Inputs/Textarea/Textarea.styles';
import { CommonTextInputStyles } from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox.styles';
import * as React from 'react';
import { createGlobalStyle } from 'styled-components';
import { CommonErrorStyles, CommonLabelStyles } from '../Form.styles';
import fonts from '@helpers/fonts';
export const UmbracoFormStyles = createGlobalStyle `
.umbraco-forms-page {
  --borderColour: ${brand.global.black};
  --textColour: ${brand.global.black};
  --altColour: ${brand.global.white};

  display: flex;
  flex-direction: column;
  margin: var(--sectionSpacing) auto;
  overflow: visible;
  background-color: ${brand.global.white};
  padding: 0 var(--sitePadding);
  max-width: calc(var(--siteWidth) + var(--sitePadding) + var(--sitePadding));

  .umbraco-forms-caption {
    ${h2};
  }

  .umbraco-forms-fieldset {
    margin: 0 0 ${fluid(16, 20)};
    width: 100%;

    > legend {
      ${h4};

      width: 100%;
      display: block;
      padding: 0 0 4px;
      border-bottom: 1px solid var(--borderColour);
      opacity: 0.8;
    }
  }
  
  .row-fluid {
    display: grid;
    gap: ${fluid(20, 24)};
    grid-template-columns: 1fr;
    width: 100%;

    @media ${from(Device.Tablet)} {
      grid-template-columns: repeat(12, 1fr);

      .col-md-1,
      .col-md-2,
      .col-md-3,
      .col-md-4 {
        grid-column: span 4;
      }
      
      .col-md-6 {
        grid-column: span 6;
      }

      .col-md-12 {
        grid-column: span 12;
      }
    }

    @media ${from(Device.TabletLarge)} {
      .col-md-1,
      .col-md-2 {
        grid-column: span 2;
      }
    }

    @media ${from(Device.DesktopLarge)} {
      .col-md-1 {
        grid-column: span 1;
      }
    }

    .umbraco-forms-container {
      display: grid;
      gap: ${fluid(32, 40)};
      grid-template-columns: 1fr;
      justify-items: flex-start;
      margin: 0 0 ${fluid(32, 40)};
      width: 100%;

      hr {
        display: block;
        border: 1px solid var(--borderColour);
        border-bottom: 0;
        grid-column: 1 / span 1;
        height: 0;
        margin: ${fluid(32, 40)} 0;
        width: 100%;

        @media ${from(Device.MobileLarge)} {
          grid-column: 1 / span 2;
        }
      }

      &:empty {
        display: none;
      }
    }

    .umbraco-forms-field {
      display: flex;
      flex-direction: column;
      width: 100%;

      &:has(input[data-val-required], textarea[data-val-required], select[data-val-required]) {
        label::after {
          content: '*';
          color: ${brand.validation.invalid};
          margin-left: 4px;
        }
      }

      &.titleanddescription,
      &.richtextfield  {
        .umbraco-forms-field-wrapper {
          padding: 20px 24px;
        }
      }

      &.checkbox,
      &.dataconsent {
        .umbraco-forms-field-wrapper {
          label {
            background-color: ${brand.primary.blue.blue25};
            border-radius: 18px;
            padding: ${fluid(16, 20)} ${fluid(16, 24)};
            width: 100%;
            font-size: 14px;
            line-height: 24px;
          }
          
        }
      }
    }

    .umbraco-forms-label,
    .umbraco-forms-legend {
      ${CommonLabelStyles};

      &:empty {
        display: none;
      }
    }

    .umbraco-forms-field-wrapper {
      display: block;

      &:has(select) {
        ${CommonDropdownWrapperStyles};
      }
    }

    .date .umbraco-forms-field-wrapper {
      ${CommonDateWrapperStyles};
    }

    .field-validation-valid,
    .field-validation-error,
    .validation-summary-errors,
    p.error  {
      ${CommonErrorStyles};
      text-align: right;

      &:empty {
        display: none;
      }

      @media ${until(Device.MobileLarge)} {
        word-break: break-word;
      }
    }

    .help-block {
      ${paragraphSmall};

      display: block;
      color: var(--textColour);
      margin: 0;
    }

    .umbraco-forms-tooltip {
      &.help-block {
        ${CommonLabelStyles};
      }
    }

    input:not([type="file"]):not([type="submit"]) {
      ${CommonTextInputStyles};

      &.input-validation-error {
        border-color: ${brand.validation.invalid};
      }
    }

    select {
      ${CommonSelectStyles};
    }

    textarea {
      ${CommonTextareaStyles};
    }

    /* File uploader styles */
    .fileupload {
      display: block;

      label {
        display: inline-block;        
      }

      /* Style the label as a button and hide default input */
      label {
        ${DefaultButtonStyles};
        --buttonHeight: ${fluid(46, 55)};
        
        --bgColour: ${brand.primary.pear.pear75};
        --bgHoverColour: ${brand.primary.pear.pear50};
        --bgActiveColour: ${brand.primary.pear.pear75};
        --borderColour: ${brand.primary.pear.pear75};
        --borderHoverColour: ${brand.primary.pear.pear50};
        --borderActiveColour: ${brand.primary.pear.pear100};
      }

      .umbraco-forms-field-wrapper {
        display: inline-block;
        margin-left: 20px;

        p {
          ${paragraphSmall};
          ${fonts.gotham.medium};
          font-size: 16px;
      
          margin: 4px 0 0;
        }

        input {
          display: none;
        }
      }
    }

    .checkboxlist {
      ${CommonCheckboxContainerStyles};
      background-color: ${brand.primary.blue.blue25};
      border-radius: 18px;
      padding: ${fluid(16, 20)} ${fluid(16, 24)};
    }

    .radiobuttonlist {
      ${CommonRadioListContainerStyles};
      background-color: ${brand.primary.blue.blue25};
      border-radius: 18px;
      padding: ${fluid(16, 20)} ${fluid(16, 24)};
    }
    
    .checkbox,
    .dataconsent,
    .checkboxlist,
    .radiobuttonlist {
      input {
        ${srOnly}
      }

      input:disabled + label {
        opacity: 0.5;
        pointer-events: none;
      }

      br {
        display: none;
      }
    }

    .checkbox,
    .dataconsent {
      input:disabled ~ label {
        opacity: 0.5;
        pointer-events: none;
      }
    } 

    .checkboxlist,
    .dataconsent .umbraco-forms-field-wrapper,
    .checkbox .umbraco-forms-field-wrapper {
      label {
        ${CommonCheckboxLabelStyles};
      }      
    }

    .dataconsent .umbraco-forms-field-wrapper input:checked ~ label::before,
    .checkbox .umbraco-forms-field-wrapper input:checked ~ label::before,
    .checkboxlist input:checked + label::before {
      color: var(--altColour);
      background: var(--textColour);
    }

    .radiobuttonlist {
      label {
        ${CommonRadioListLabelStyles};
      }
      
      input:checked + label::before {
        background-color: var(--textColour);
      }
    }

    .titleanddescription .umbraco-forms-field-wrapper,
    .richtextfield {
      > div {
        ${contentStyles};
      }
    }
  }

  .umbraco-forms-navigation > div {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  input.btn {
    ${CommonButtonTextStyles};
    ${DefaultButtonStyles};

    &.prev {
      margin-right: auto;
    }
  }

  .umbraco-forms-hidden {
    display: none;
  }
}

`;
const UmbracoForm = () => {
    return React.createElement(UmbracoFormStyles, null);
};
export default UmbracoForm;
